import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Release } from '../models/release.model';
import { ReleaseAssessment, ReleaseDecisionRequest, SignoffRole } from '../models/release-assessment.model';

/**
 * Das aktuelle Release eines Projekts: das in Abnahme befindliche mit dem jüngsten
 * Stichtag. Releases ohne Stichtag zählen nur, wenn keines in Abnahme einen hat —
 * dann gilt das erste in Listenreihenfolge.
 */
export function pickCurrentRelease(releases: readonly Release[]): Release | null {
  const inAcceptance = releases.filter((r) => r.status === 'IN_ABNAHME');
  const dated = inAcceptance.filter((r) => !!r.stichtag);
  if (dated.length === 0) return inAcceptance[0] ?? null;
  return dated.reduce((newest, r) => ((r.stichtag ?? '') > (newest.stichtag ?? '') ? r : newest));
}

@Injectable({ providedIn: 'root' })
export class ReleaseService {
  private http = inject(HttpClient);
  private base = `${environment.apiBaseUrl}/api/v1/projects`;

  /** Liefert die Releases des Projekts, neueste (nach Stichtag) zuerst. */
  list(projectId: string): Observable<Release[]> {
    return this.http.get<Release[]>(`${this.base}/${projectId}/releases`);
  }

  /** Das aktuelle Release (siehe {@link pickCurrentRelease}); `null`, wenn keines in Abnahme ist. */
  currentRelease(projectId: string): Observable<Release | null> {
    return this.list(projectId).pipe(map(pickCurrentRelease));
  }

  getById(projectId: string, releaseId: string): Observable<Release> {
    return this.http.get<Release>(this.releaseUrl(projectId, releaseId));
  }

  getAssessment(projectId: string, releaseId: string): Observable<ReleaseAssessment> {
    return this.http.get<ReleaseAssessment>(`${this.releaseUrl(projectId, releaseId)}/assessment`);
  }

  saveDecision(projectId: string, releaseId: string, body: ReleaseDecisionRequest): Observable<ReleaseAssessment> {
    return this.http.put<ReleaseAssessment>(`${this.releaseUrl(projectId, releaseId)}/decision`, body);
  }

  confirmSignoff(projectId: string, releaseId: string, rolle: SignoffRole): Observable<ReleaseAssessment> {
    return this.http.post<ReleaseAssessment>(
      `${this.releaseUrl(projectId, releaseId)}/signoffs/${encodeURIComponent(rolle)}/confirm`,
      null,
    );
  }

  private releaseUrl(projectId: string, releaseId: string): string {
    return `${this.base}/${projectId}/releases/${encodeURIComponent(releaseId)}`;
  }
}
